import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    SquaresFour, Scales, UsersThree, FileText, ListChecks, Clock,
    Gear, SignOut, Play, Stop, MagnifyingGlass,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { fmtCurrency } from "@/lib/utils-domain";
import { Toaster } from "@/components/ui/sonner";
import { BRAND_NAME, BRAND_KICKER } from "@/lib/brand";

const NAV = [
    { to: "/app", label: "Dashboard", icon: SquaresFour, end: true },
    { to: "/app/cases", label: "Cases", icon: Scales },
    { to: "/app/crm", label: "Intake CRM", icon: UsersThree },
    { to: "/app/documents", label: "Documents", icon: FileText },
    { to: "/app/tasks", label: "Tasks", icon: ListChecks },
    { to: "/app/billing", label: "Time & Billing", icon: Clock },
];

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [activeTimer, setActiveTimer] = useState(null);
    const [elapsed, setElapsed] = useState(0);

    const loadTimer = async () => {
        try {
            const { data } = await api.get("/time-entries/active");
            setActiveTimer(data || null);
        } catch {
            setActiveTimer(null);
        }
    };
    useEffect(() => {
        loadTimer();
        const i = setInterval(loadTimer, 30000);
        return () => clearInterval(i);
    }, []);

    useEffect(() => {
        if (!activeTimer) {
            setElapsed(0);
            return;
        }
        const tick = () => {
            const started = new Date(activeTimer.started_at).getTime();
            setElapsed(Math.floor((Date.now() - started) / 1000));
        };
        tick();
        const i = setInterval(tick, 1000);
        return () => clearInterval(i);
    }, [activeTimer]);

    const stopTimer = async () => {
        if (!activeTimer) return;
        await api.post("/time-entries/stop", { entry_id: activeTimer.entry_id });
        setActiveTimer(null);
    };

    const fmtElapsed = (s) => {
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    };

    return (
        <div className="min-h-screen flex bg-slate-50 text-slate-900">
            {/* Sidebar */}
            <aside className="hidden md:flex w-60 flex-col bg-[#0B2447] text-slate-200" data-testid="sidebar">
                <Link to="/app" className="px-6 pt-6 pb-8 block">
                    <div className="font-display text-2xl font-extrabold text-white tracking-tight">{BRAND_NAME}</div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mt-1">{BRAND_KICKER}</div>
                </Link>
                <nav className="flex-1 px-3 space-y-0.5">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.end}
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                                    isActive
                                        ? "bg-white/10 text-white border-l-2 border-blue-400"
                                        : "text-slate-300 hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            <n.icon size={18} weight="duotone" />
                            {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-white/10">
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mb-2">Firm</div>
                    <div className="text-sm text-white truncate">{user?.firm_name || user?.name}</div>
                    <div className="text-xs text-slate-400 truncate">{user?.email}</div>
                    <button
                        onClick={async () => { await logout(); navigate("/login"); }}
                        data-testid="logout-button"
                        className="mt-4 w-full flex items-center justify-center gap-2 text-xs text-slate-300 hover:text-white border border-white/10 rounded-sm py-2 transition"
                    >
                        <SignOut size={14} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                <header className="h-14 bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-40 flex items-center px-6 gap-6">
                    <div className="flex items-center gap-2 flex-1">
                        <MagnifyingGlass size={16} className="text-slate-400" />
                        <input
                            placeholder="Search cases, clients, documents…"
                            className="bg-transparent text-sm w-full focus:outline-none placeholder:text-slate-400"
                            data-testid="global-search"
                        />
                    </div>
                    {activeTimer && (
                        <div className="flex items-center gap-3 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="active-timer-pill">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="font-mono text-xs text-emerald-800 font-semibold">{fmtElapsed(elapsed)}</span>
                            <span className="text-xs text-emerald-700">·</span>
                            <span className="text-xs text-emerald-700 font-mono">
                                {fmtCurrency((elapsed / 3600) * (user?.hourly_rate || 300))}
                            </span>
                            <button onClick={stopTimer} className="text-emerald-800 hover:text-emerald-900" data-testid="stop-timer-button">
                                <Stop size={14} weight="fill" />
                            </button>
                        </div>
                    )}
                    {!activeTimer && (
                        <Link
                            to="/app/billing"
                            className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900"
                            data-testid="header-start-timer"
                        >
                            <Play size={14} weight="fill" /> Start timer
                        </Link>
                    )}
                    <Link to="/app/settings" className="text-slate-500 hover:text-slate-800" data-testid="settings-link">
                        <Gear size={18} />
                    </Link>
                </header>
                <main className="flex-1 min-w-0 p-6 lg:p-10">
                    <Outlet />
                </main>
            </div>
            <Toaster richColors position="top-right" />
        </div>
    );
}
